import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { useEffect, useMemo, useState } from 'react';
import { apiClient, ApiError } from '@/utils/api';
import { ApiErrorCode } from '@portafino/shared';
import { RoutePath } from '@/utils/routes';
import GlobalLoader from './GlobalLoader';
import { updateBundlerAuth } from '@/config/wagmi';

// A passkey that does not verify answers 401 as well. That is a refused sign-in on the page the
// person is already on — the console login, or registering from an invite — not a session that
// ended, so it must not send them to the tenant login.
const PASSKEY_SIGN_IN_ENDPOINTS = ['/tenants/auth/complete-challenge', '/root-admins/auth/complete-challenge'];

export function endsSession(error: ApiError): boolean {
  if (PASSKEY_SIGN_IN_ENDPOINTS.some((path) => error.config?.url?.endsWith(path))) {
    return false;
  }
  return error.response?.status === 401 || error.response?.data?.code === ApiErrorCode.CONTEXT_HEADER_NOT_FOUND;
}

interface AuthGuardProps {
  children: React.ReactNode;
  allowedRoles?: Array<'root_admin' | 'admin'>;
}

export function AuthGuard({ children, allowedRoles }: AuthGuardProps) {
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  const onlyRootAdminAllowed = allowedRoles?.length === 1 && allowedRoles[0] === 'root_admin';
  const notSignedInRedirect = useMemo(() => {
    if (onlyRootAdminAllowed) {
      return RoutePath.RootAdminLogin;
    }
    else if (allowedRoles?.length) {
      return RoutePath.Login;
    }

    return null;
  }, [onlyRootAdminAllowed, allowedRoles])

  const loginRoute = useMemo(() => {
    if (onlyRootAdminAllowed) {
      return RoutePath.RootAdminLogin;
    }
    return RoutePath.Login;
  }, [onlyRootAdminAllowed])

  useEffect(() => {
    // A user restored from storage has no token (it is never persisted) until GianoWatcher
    // signs them in again; send no Authorization header meanwhile.
    if (user?.accessToken) {
      apiClient.setBearerToken(user.accessToken)
      updateBundlerAuth(user.accessToken)
    }
    else {
      updateBundlerAuth(null)
      apiClient.clearBearerToken()
    }
    setReady(true)
  }, [user])

  useEffect(() => {
    apiClient.setErrorInterceptor((error) => {
      if (endsSession(error)) {
        apiClient.clearBearerToken()
        if (loginRoute) {
          navigate(loginRoute)
        }
      }
      return Promise.reject(error)
    })
  }, [loginRoute, navigate])

  if (!user && notSignedInRedirect) {
    return <Navigate to={notSignedInRedirect} state={{ from: location }} replace />;
  }

  if (user && allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={RoutePath.Unauthorized} replace />;
  }

  if (!ready) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <GlobalLoader />
      </div>
    )
  }

  return <>{children}</>
}
